import { useEffect, useState } from "react";
import { Scissors } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/design-system";
import { DocumentViewer } from "@/components/ui/document-workspace";
import { api, errorMessage } from "@/lib/api";
import { InboxThumbnail, loadInboxPages } from "./InboxPages";
import "./InboxSplitDialog.css";

const colors = ["#2563eb", "#b45309", "#15803d", "#9333ea", "#be123c", "#0e7490"];
export function InboxSplitDialog({ id, company = false, pages: suppliedPages, starts, onChange, onClose, onConfirm, disabled, reason = "", error, singleCut = false }: {
  id: string; company?: boolean; pages?: { page: number }[]; starts: number[];
  onChange: (starts: number[]) => void; onClose: () => void; onConfirm: () => void;
  disabled: boolean; reason?: string; error?: string | null; singleCut?: boolean;
}) {
  const [loaded, setLoaded] = useState<{ page: number }[]>([]);
  const [loadError, setLoadError] = useState("");
  const [preview, setPreview] = useState<number | null>(null);
  useEffect(() => {
    document.querySelector<HTMLElement>(".inbox-split-modal [role='dialog']")?.focus();
  }, [preview]);
  useEffect(() => {
    const root = document.getElementById("root");
    const wasInert = root?.inert ?? false;
    const overflow = document.body.style.overflow;
    if (root) root.inert = true;
    document.body.style.overflow = "hidden";
    return () => { if (root) root.inert = wasInert; document.body.style.overflow = overflow; };
  }, []);
  useEffect(() => {
    if (suppliedPages) return;
    const controller = new AbortController();
    loadInboxPages(id, controller.signal, company).then(value => { if (!controller.signal.aborted) setLoaded(value); })
      .catch(cause => { if (!controller.signal.aborted) setLoadError(errorMessage(cause, "Stranice se ne mogu učitati.")); });
    return () => controller.abort();
  }, [id, company, suppliedPages]);
  const pages = suppliedPages ?? loaded;
  const boundaries = [1, ...starts, pages.length + 1];
  const parts = boundaries.slice(0, -1).map((first, index) => {
    const last = boundaries[index + 1]! - 1;
    return `Dokument ${index + 1} (str. ${first === last ? first : `${first}–${last}`})`;
  });
  const blocked = reason || loadError || (!pages.length ? "Stranice se učitavaju." : !starts.length ? "Postavite barem jedan rez između stranica." : "");
  function toggle(page: number) {
    onChange(starts.includes(page) ? starts.filter(value => value !== page) : singleCut ? [page] : [...starts, page].sort((a, b) => a - b));
  }
  return <Dialog fullscreen className="inbox-split-modal" title="Razdvajanje dokumenta" onClose={() => { if (!disabled) { if (preview) setPreview(null); else onClose(); } }} actions={<div className="inbox-split-footer">
    <p aria-label="Pregled dijelova" className="inbox-split-summary">{parts.join(" · ")}</p>
    {blocked && <p role="status">{blocked}</p>}
    {error && <p role="alert">{error}</p>}
    <div className="flex flex-wrap gap-2"><Button type="button" disabled={disabled || !!blocked} onClick={onConfirm}>Razdvoji na {parts.length} dokumenata</Button><Button type="button" variant="outline" disabled={disabled} onClick={onClose}>Odustani</Button></div>
  </div>}>
    {preview ? <div className="inbox-split-preview"><Button type="button" variant="outline" onClick={() => setPreview(null)}>Natrag na stranice</Button><SplitPagePreview id={id} page={preview} company={company} /></div> : <div className="inbox-split-scroll">
      <p>Klikni između stranica gdje počinje novi dokument.</p>
      <p className="font-semibold" aria-live="polite">Broj dijelova: {parts.length}</p>
      {singleCut && <p>Sken se razdvaja jednim rezom. Za dodatne dijelove ponovite razdvajanje.</p>}
      <div className="inbox-split-grid">{pages.map(({ page }) => {
        const part = starts.filter(start => start <= page).length;
        return <div key={page} className="inbox-split-cell">
          {page > 1 ? <Button type="button" variant="outline" className="inbox-split-cut" aria-label={`Novi dokument od ove stranice: ${page}`} aria-pressed={starts.includes(page)} disabled={disabled} onClick={() => toggle(page)}><Scissors size={18} aria-hidden="true" /><span>Novi dokument od ove stranice</span></Button> : <div className="inbox-split-first">Početak dokumenta</div>}
          <div className="inbox-split-page" style={{ borderColor: colors[part % colors.length] }}>
            <p>{parts[part]}</p>
            <button type="button" className="inbox-split-image" aria-label={`Povećaj stranicu ${page}`} onClick={() => setPreview(page)}><InboxThumbnail id={id} page={page} company={company} className="inbox-split-thumbnail" /></button>
            <p>Stranica {page}</p>
          </div>
        </div>;
      })}</div>
    </div>}
  </Dialog>;
}

function SplitPagePreview({ id, page, company }: { id: string; page: number; company: boolean }) {
  const [url, setUrl] = useState(""); const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController(); let objectUrl = "";
    void (async () => {
      const blob = (await api.get<Blob>(`${company ? "/company-inbox" : "/inbox"}/${id}/content`, { responseType: "blob", signal: controller.signal })).data;
      const { renderPdfPage } = await import("@/components/site-planning/pdfDrawing");
      const image = await renderPdfPage(blob, page, controller.signal);
      if (!controller.signal.aborted) { objectUrl = URL.createObjectURL(image); setUrl(objectUrl); }
    })().catch(cause => { if (!controller.signal.aborted) setError(errorMessage(cause, "Pregled stranice nije dostupan.")); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id, page, company]);
  return url ? <DocumentViewer url={url} mime="image/png" title={`Stranica ${page}`} /> : <p role={error ? "alert" : "status"}>{error || "Učitavanje stranice…"}</p>;
}
